import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Info, User, FileText, MapPin, Briefcase, Gift, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Checkbox } from '@travellegacy/nexops-design-system';
import { useCreateEmployee, useDepartments, useJobPositions } from '../hooks/useHCM';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@travellegacy/nexops-design-system';
import { Link } from 'react-router-dom';
import { applyCPFMask, applyPhoneMask, applyCEPMask, removeMask } from '@/shared/lib/utils';

export function NewEmployeePage() {
  const navigate = useNavigate();
  const createEmployee = useCreateEmployee();
  const { data: departments = [] } = useDepartments();
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const { data: jobPositions = [] } = useJobPositions({
    departmentId: selectedDepartment || undefined,
  });

  const [formData, setFormData] = useState({
    // User data
    name: '',
    email: '',
    password: '',

    // Dados pessoais
    rg: '',
    cpf: '',
    birthDate: '',
    phone: '',
    pixKey: '',
    bankAccount: '',

    // Endereço
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',

    // Dados contratuais
    admissionDate: '',
    contractType: 'CLT' as 'CLT' | 'PJ',
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE' | 'VACATION',
    jobPositionId: '',
    managerId: '',

    // Benefícios
    benefitVAVR: null as number | null,
    benefitHomeOffice: null as number | null,
    benefitVT: null as number | null,
    benefitVTDiscount: 0,
    benefitVTPaymentMethod: 'PLUXEE' as 'PLUXEE' | 'PIX',
    hasGympass: false,

    // Salário inicial
    initialSalary: '', // Mantido para compatibilidade
    initialEmployeeSalary: '',
    salaryReason: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingCep, setLoadingCep] = useState(false);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Handlers especiais com máscaras
  const handleCPFChange = (value: string) => {
    const formatted = applyCPFMask(value);
    setFormData((prev) => ({ ...prev, cpf: formatted }));
    if (errors.cpf) {
      setErrors((prev) => ({ ...prev, cpf: '' }));
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = applyPhoneMask(value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  // Buscar endereço pelo CEP
  const fetchAddressByCep = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      return;
    }

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setErrors((prev) => ({ ...prev, zipCode: 'CEP não encontrado' }));
        return;
      }

      // Preencher os campos automaticamente
      setFormData((prev) => ({
        ...prev,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
        complement: data.complemento || prev.complement,
      }));

      // Limpar erro de CEP se houver
      setErrors((prev) => ({ ...prev, zipCode: '' }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setErrors((prev) => ({ ...prev, zipCode: 'Erro ao buscar CEP' }));
    } finally {
      setLoadingCep(false);
    }
  };

  // Handler especial para CEP
  const handleCepChange = (value: string) => {
    const formatted = applyCEPMask(value);
    setFormData((prev) => ({ ...prev, zipCode: formatted }));

    if (errors.zipCode) {
      setErrors((prev) => ({ ...prev, zipCode: '' }));
    }

    // Buscar endereço quando CEP estiver completo
    const cleanCep = removeMask(formatted);
    if (cleanCep.length === 8) {
      fetchAddressByCep(formatted);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    }

    if (formData.cpf) {
      const cpfNumbers = removeMask(formData.cpf);
      if (cpfNumbers.length !== 11) {
        newErrors.cpf = 'CPF deve ter 11 dígitos';
      }
    }

    if (formData.state && formData.state.length !== 2) {
      newErrors.state = 'Estado deve ter 2 letras (ex: SP)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const payload: Record<string, unknown> = {
        name: formData.name,
        email: formData.email,
      };

      // Adicionar campos opcionais apenas se preenchidos (removendo máscaras)
      if (formData.password) payload.password = formData.password;
      if (formData.rg) payload.rg = formData.rg;
      if (formData.cpf) payload.cpf = removeMask(formData.cpf);
      if (formData.birthDate) payload.birthDate = formData.birthDate;
      if (formData.phone) payload.phone = removeMask(formData.phone);
      if (formData.pixKey) payload.pixKey = formData.pixKey;
      if (formData.bankAccount) payload.bankAccount = formData.bankAccount;

      if (formData.zipCode) payload.zipCode = removeMask(formData.zipCode);
      if (formData.street) payload.street = formData.street;
      if (formData.number) payload.number = formData.number;
      if (formData.complement) payload.complement = formData.complement;
      if (formData.neighborhood) payload.neighborhood = formData.neighborhood;
      if (formData.city) payload.city = formData.city;
      if (formData.state) payload.state = formData.state;

      if (formData.admissionDate) payload.admissionDate = formData.admissionDate;
      payload.contractType = formData.contractType;
      payload.status = formData.status;
      if (formData.jobPositionId) payload.jobPositionId = formData.jobPositionId;
      if (formData.managerId) payload.managerId = formData.managerId;

      payload.benefitVAVR = formData.benefitVAVR;
      payload.benefitHomeOffice = formData.benefitHomeOffice;
      payload.benefitVT = formData.benefitVT;
      payload.benefitVTDiscount = formData.benefitVTDiscount;
      payload.benefitVTPaymentMethod = formData.benefitVTPaymentMethod;
      payload.hasGympass = formData.hasGympass;

      // Salário inicial - enviar os novos campos
      if (formData.initialEmployeeSalary) {
        payload.initialSalary = Number(formData.initialEmployeeSalary); // Compatibilidade
        payload.initialEmployeeSalary = Number(formData.initialEmployeeSalary);
        payload.salaryReason = formData.salaryReason || 'Salário inicial';
      }

      const createdEmployee = await createEmployee.mutateAsync(payload);
      // Redirecionar para página de edição onde pode configurar comissões
      navigate(`/people-core/hcm/employees/${createdEmployee.id}/edit`);
    } catch (error) {
      console.error('Erro ao criar colaborador:', error);
      setErrors({ submit: 'Erro ao criar colaborador. Verifique os dados e tente novamente.' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Novo Colaborador</h1>
          <p className="text-muted-foreground">Cadastro de novo colaborador HCM</p>
        </div>
        <Link to="/people-core/hcm/employees" className={cn(buttonVariants({ variant: 'outline' }))}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Dados de Usuário */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Dados de Usuário
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Nome Completo *"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={errors.name}
                  required
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={errors.email}
                  required
                />
                <Input
                  label="Senha (opcional, será gerada automaticamente se vazio)"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  error={errors.password}
                />
              </div>
            </CardContent>
          </Card>

          {/* Dados Pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Dados Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label="CPF"
                  value={formData.cpf}
                  onChange={(e) => handleCPFChange(e.target.value)}
                  error={errors.cpf}
                  maxLength={14}
                  placeholder="000.000.000-00"
                />
                <Input
                  label="RG"
                  value={formData.rg}
                  onChange={(e) => handleChange('rg', e.target.value)}
                  placeholder="00.000.000-0"
                />
                <Input
                  label="Data de Nascimento"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                />
                <Input
                  label="Telefone (com DDI)"
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  maxLength={19}
                  placeholder="+55 (00) 00000-0000"
                />
                <Input
                  label="Chave Pix"
                  value={formData.pixKey}
                  onChange={(e) => handleChange('pixKey', e.target.value)}
                />
                <Input
                  label="Conta Bancária"
                  value={formData.bankAccount}
                  onChange={(e) => handleChange('bankAccount', e.target.value)}
                  placeholder="Banco, Ag, Conta"
                />
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Endereço
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label={loadingCep ? 'CEP (buscando...)' : 'CEP'}
                  value={formData.zipCode}
                  onChange={(e) => handleCepChange(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  error={errors.zipCode}
                  disabled={loadingCep}
                />
                <div className="md:col-span-2">
                  <Input
                    label="Rua"
                    value={formData.street}
                    onChange={(e) => handleChange('street', e.target.value)}
                  />
                </div>
                <Input
                  label="Número"
                  value={formData.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                />
                <Input
                  label="Complemento"
                  value={formData.complement}
                  onChange={(e) => handleChange('complement', e.target.value)}
                />
                <Input
                  label="Bairro"
                  value={formData.neighborhood}
                  onChange={(e) => handleChange('neighborhood', e.target.value)}
                />
                <Input
                  label="Cidade"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
                <Input
                  label="Estado (UF)"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value.toUpperCase())}
                  error={errors.state}
                  maxLength={2}
                  placeholder="SP"
                />
              </div>
            </CardContent>
          </Card>

          {/* Dados Contratuais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Dados Contratuais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Data de Admissão"
                  type="date"
                  value={formData.admissionDate}
                  onChange={(e) => handleChange('admissionDate', e.target.value)}
                />
                <Select
                  label="Tipo de Contrato"
                  options={[
                    { label: 'CLT', value: 'CLT' },
                    { label: 'PJ', value: 'PJ' },
                  ]}
                  value={formData.contractType}
                  onChange={(e) => handleChange('contractType', e.target.value)}
                />
                <Select
                  label="Status"
                  options={[
                    { label: 'Ativo', value: 'ACTIVE' },
                    { label: 'Inativo', value: 'INACTIVE' },
                    { label: 'Férias', value: 'VACATION' },
                  ]}
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                />
                <Select
                  label="Departamento"
                  options={[
                    { label: 'Selecione um departamento', value: '' },
                    ...departments.map((dept) => ({ label: dept.name, value: dept.id })),
                  ]}
                  value={selectedDepartment}
                  onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    handleChange('jobPositionId', '');
                  }}
                />
                <Select
                  label="Cargo"
                  options={[
                    { label: 'Selecione um cargo', value: '' },
                    ...jobPositions.map((pos) => ({ label: pos.name, value: pos.id })),
                  ]}
                  value={formData.jobPositionId}
                  onChange={(e) => handleChange('jobPositionId', e.target.value)}
                  disabled={!selectedDepartment}
                />
              </div>
            </CardContent>
          </Card>

          {/* Benefícios */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5" />
                Benefícios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="VA/VR (Valor Mensal)"
                    type="number"
                    placeholder="Ex: 500.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitVAVR || ''}
                    onChange={(e) => handleChange('benefitVAVR', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                  <Input
                    label="Auxílio Home Office (Valor Mensal)"
                    type="number"
                    placeholder="Ex: 200.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitHomeOffice || ''}
                    onChange={(e) => handleChange('benefitHomeOffice', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                  <Input
                    label="VT - Valor Fornecido (Mensal)"
                    type="number"
                    placeholder="Ex: 300.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitVT || ''}
                    onChange={(e) => handleChange('benefitVT', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
                <div>
                  <Input
                    label="VT - Desconto em Folha"
                    type="number"
                    placeholder="Ex: 150.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitVTDiscount}
                    onChange={(e) => handleChange('benefitVTDiscount', parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Valor que será descontado da participação/salário do colaborador
                  </p>
                </div>
                <div>
                  <Select
                    label="VT - Pago via"
                    options={[
                      { label: 'Pluxee (cartão de benefícios)', value: 'PLUXEE' },
                      { label: 'Pix (junto do pagamento)', value: 'PIX' },
                    ]}
                    value={formData.benefitVTPaymentMethod}
                    onChange={(e) => handleChange('benefitVTPaymentMethod', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    No Pix, o VT entra automaticamente em Adicionais na página de Pagamentos e sai da aba Benefícios
                  </p>
                </div>
                <Checkbox
                  id="hasGympass"
                  label="Gympass"
                  checked={formData.hasGympass}
                  onChange={(checked) => handleChange('hasGympass', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Salário Inicial */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Salário Inicial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Salário do Colaborador (R$)"
                  type="number"
                  value={formData.initialEmployeeSalary}
                  onChange={(e) => handleChange('initialEmployeeSalary', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  helperText="Valor bruto que aparece para o colaborador"
                />
                <Input
                  label="Motivo"
                  value={formData.salaryReason}
                  onChange={(e) => handleChange('salaryReason', e.target.value)}
                  placeholder="Salário inicial"
                />
              </div>
            </CardContent>
          </Card>

          {/* Info sobre comissões */}
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Configuração de Comissões
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Após salvar o colaborador, você será redirecionado para a página de edição onde poderá configurar as regras de comissões para cada tipo de serviço (Voos, Hospedagens, Seguros, etc.).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {errors.submit && (
            <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Link to="/people-core/hcm/employees" className={cn(buttonVariants({ variant: 'outline' }))}>
              Cancelar
            </Link>
            <Button type="submit" disabled={createEmployee.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {createEmployee.isPending ? 'Salvando...' : 'Salvar Colaborador'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
